import _generalSearch from "@zenlayer/zen-design-icons/assets/general/Search.svg";
import _suggestInfoCircleLine from "@zenlayer/zen-design-icons/assets/suggest/InfoCircleLine.svg";
import _suggestCloseCircleFill from "@zenlayer/zen-design-icons/assets/suggest/CloseCircleFill.svg";
import { useCreation, useDebounce, useFocusWithin, useMemoizedFn, useSize } from 'ahooks';
import classnames from 'classnames';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { isString } from '../_util/defineType';
import { calc, pickDomAttrs } from '../_util/utils';
import { useConfig } from '../hooks/useConfig';
import Icon from '../icon';
import GroupSearch from './groupSearch';
import TextSearch from './textSearch';
import useTipModal from './useTipModal';
import { formatValueText } from './utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const SuperSearch = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    locale: {
      SuperSearch: SuperSearchLocale
    }
  } = useConfig();
  const tipModal = useTipModal();
  const {
    defaultValue,
    value,
    configuration = [],
    width = 720,
    placeholder,
    onSearch,
    prefixClass = 'zen-design',
    //TODO 写provider时统一改掉
    className,
    style,
    ...restProps
  } = props;
  const baseClassName = "".concat(prefixClass, "-super-search");
  const attrs = pickDomAttrs(restProps);
  const conf = useCreation(() => {
    let hasMultipleItem = false;
    const items = configuration.map(item => {
      var _item$multiple;
      // 以下类型默认开启批量匹配
      const defaultMultipleMatch = ['uuid', 'uid-32', 'uid-num'];
      const multiple = (_item$multiple = item.multiple) !== null && _item$multiple !== void 0 ? _item$multiple : !!item.match && defaultMultipleMatch.includes(item.match);
      if (multiple) {
        hasMultipleItem = true;
      }
      return {
        ...item,
        multiple,
        mode: item.options ? 'select' : 'text'
      };
    });
    const onlyOneTextItem = items.length === 1 && items[0].mode === 'text';
    return {
      items,
      hasMultipleItem,
      onlyOneTextItem
    };
  }, [configuration]);
  const val = useCreation(() => {
    if (value) {
      return Object.keys(value).reduce((acc, key) => {
        const v = value[key];
        const item = conf.items.find(it => it.multiple && it.key === key);
        if (isString(v) && item) {
          acc[key] = formatValueText(v, item);
        } else {
          acc[key] = v;
        }
        return acc;
      }, {});
    }
    return undefined;
  }, [conf.items, JSON.stringify(value)]);
  const defaultVal = useCreation(() => {
    if (defaultValue) {
      return Object.keys(defaultValue).reduce((acc, key) => {
        const v = defaultValue[key];
        const item = conf.items.find(it => it.multiple && it.key === key);
        if (isString(v) && item) {
          acc[key] = formatValueText(v, item);
        } else {
          acc[key] = v;
        }
        return acc;
      }, {});
    }
    return undefined;
  }, [conf.items, JSON.stringify(defaultValue)]);
  const [clearIconVisible, toggleClearIcon] = useState(false);
  const searchInputRef = useRef(null);
  const contentRef = useRef(null);
  const contentSize = useSize(contentRef);
  const isFocusWithin = useFocusWithin(contentRef);
  const debounceIsFocusWithin = useDebounce(isFocusWithin, {
    wait: 100
  }); // 使用debounce，防止焦点切换时，ui闪烁

  const overflowIconVisible = useCreation(() => {
    var _contentRef$current;
    if (debounceIsFocusWithin || !contentSize) {
      return false;
    }
    let tagWidthSum = 0;
    (_contentRef$current = contentRef.current) === null || _contentRef$current === void 0 || _contentRef$current.querySelectorAll('[data-super-search-content="1"]').forEach((node, idx) => {
      tagWidthSum = calc.add(tagWidthSum, idx ? 8 : 0, node.getBoundingClientRect().width);
    });
    return tagWidthSum > contentSize.width;
  }, [debounceIsFocusWithin, contentSize === null || contentSize === void 0 ? void 0 : contentSize.width]);
  const focus = useMemoizedFn(() => {
    var _searchInputRef$curre;
    (_searchInputRef$curre = searchInputRef.current) === null || _searchInputRef$curre === void 0 || _searchInputRef$curre.focus();
  });
  const blur = useMemoizedFn(() => {
    var _searchInputRef$curre2;
    (_searchInputRef$curre2 = searchInputRef.current) === null || _searchInputRef$curre2 === void 0 || _searchInputRef$curre2.blur();
  });
  const clear = useMemoizedFn(() => {
    var _searchInputRef$curre3;
    (_searchInputRef$curre3 = searchInputRef.current) === null || _searchInputRef$curre3 === void 0 || _searchInputRef$curre3.clear();
  });
  const suffixElement = useCreation(() => {
    const elements = [];
    if (debounceIsFocusWithin && clearIconVisible) {
      elements.push( /*#__PURE__*/_jsx("span", {
        className: "action-icon",
        onMouseDown: () => {
          window.setTimeout(clear, 0);
        },
        onClick: e => {
          // 防止冒泡触发最外层div的onClick导致focus
          e.stopPropagation();
        },
        children: /*#__PURE__*/_jsx(Icon, {
          type: "suggest/CloseCircleFill",
          block: true
        })
      }, "clear"));
    }
    if (overflowIconVisible) {
      elements.push( /*#__PURE__*/_jsx("span", {
        className: "overflow-text",
        children: "..."
      }, "overflow"));
    }
    if (conf.hasMultipleItem && debounceIsFocusWithin) {
      elements.push( /*#__PURE__*/_jsx("span", {
        className: "action-icon",
        onMouseDown: () => {
          window.setTimeout(() => {
            tipModal.open({
              baseClassName,
              text: SuperSearchLocale
            });
          }, 100);
        },
        onClick: e => {
          // 防止冒泡触发最外层div的onClick导致focus
          e.stopPropagation();
        },
        children: /*#__PURE__*/_jsx(Icon, {
          type: "suggest/InfoCircleLine",
          block: true
        })
      }, "information"));
    }
    return elements.length ? /*#__PURE__*/_jsx("div", {
      className: "".concat(baseClassName, "--suffix"),
      children: elements
    }) : null;
  }, [conf.hasMultipleItem, debounceIsFocusWithin, clearIconVisible, overflowIconVisible]);

  // 提供自定义的 ref
  useImperativeHandle(ref, () => ({
    focus,
    clear,
    blur
  }));
  return /*#__PURE__*/_jsxs("div", {
    ...attrs.data,
    className: classnames(baseClassName, className, {
      ['focus-within']: debounceIsFocusWithin
    }),
    style: {
      ...style,
      width,
      maxHeight: conf.onlyOneTextItem ? 32 : undefined
    },
    onClick: focus,
    children: [/*#__PURE__*/_jsx("div", {
      className: "".concat(baseClassName, "--prefix"),
      children: /*#__PURE__*/_jsx(Icon, {
        type: "general/Search",
        block: true,
        className: "search-icon"
      })
    }), /*#__PURE__*/_jsx("div", {
      ref: contentRef,
      className: "".concat(baseClassName, "--content"),
      children: conf.onlyOneTextItem ? /*#__PURE__*/_jsx(TextSearch, {
        ref: searchInputRef,
        defaultValue: defaultVal === null || defaultVal === void 0 ? void 0 : defaultVal[conf.items[0].key],
        value: val === null || val === void 0 ? void 0 : val[conf.items[0].key],
        placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : SuperSearchLocale.placeholder.empty.blur + conf.items[0].label,
        multiple: conf.items[0].multiple,
        baseClassName: baseClassName,
        onSearch: v => {
          onSearch({
            [conf.items[0].key]: v
          });
        },
        toggleClearIcon: toggleClearIcon
      }) : /*#__PURE__*/_jsx(GroupSearch, {
        ref: searchInputRef,
        defaultValue: defaultVal,
        value: val,
        configuration: conf.items,
        placeholder: placeholder,
        baseClassName: baseClassName,
        isFocusWithin: debounceIsFocusWithin,
        onSearch: onSearch,
        toggleClearIcon: toggleClearIcon
      })
    }), suffixElement]
  });
});
export default SuperSearch;